import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, ChevronLeft, ChevronRight, Loader2, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SiteLogo from '../components/Layout/SiteLogo';
import LanguageSwitcher from '../components/Layout/LanguageSwitcher';
import { useSettings } from '../contexts/SettingsContext';
import {
  buildNifReviewSummary,
  emptyNifFormData,
  NifFormData,
  NIF_WIZARD_STEPS,
  NifWizardStepId,
  validateNifStep
} from './NIF/nifFormHelpers';
import { Button } from '../components/UI';

const ACCEPT_DOCS = '.pdf,.jpg,.jpeg,.png';
const MAX_FILE_MB = 5;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

const stepFieldsClass = 'space-y-5 sm:space-y-4 min-w-0';
const pageShell = 'min-h-[100dvh] bg-slate-100';

const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({
  children,
  required
}) => (
  <span className="visa-form-label">
    {children}
    {required && <span className="text-red-500"> *</span>}
  </span>
);

const TextField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoComplete?: string;
  maxLength?: number;
}> = ({
  label,
  value,
  onChange,
  type = 'text',
  required,
  placeholder,
  inputMode,
  autoComplete,
  maxLength
}) => (
  <label className="visa-form-field block">
    <FieldLabel required={required}>{label}</FieldLabel>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      inputMode={inputMode}
      autoComplete={autoComplete}
      maxLength={maxLength}
      className={`visa-form-input ${type === 'date' ? 'visa-form-input--date' : ''}`}
    />
  </label>
);

const NIF: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [siteName, setSiteName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const displaySiteName = settings?.site?.name || siteName || t('common.siteNameFallback');

  const [formData, setFormData] = useState<NifFormData>(emptyNifFormData);
  const [stepIndex, setStepIndex] = useState(0);
  const [stepError, setStepError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [documentoPassaporte, setDocumentoPassaporte] = useState<File | null>(null);
  const [documentoComprovanteResidencia, setDocumentoComprovanteResidencia] = useState<File | null>(
    null
  );

  const steps = NIF_WIZARD_STEPS;
  const currentStep = steps[stepIndex] ?? 'personal';
  const progress = ((stepIndex + 1) / steps.length) * 100;

  const reviewSections = useMemo(
    () =>
      buildNifReviewSummary(formData, t, {
        passport: documentoPassaporte,
        residence: documentoComprovanteResidencia
      }),
    [formData, documentoPassaporte, documentoComprovanteResidencia, t, i18n.language]
  );

  useEffect(() => {
    if (!token) {
      setErrorMsg(t('nif.linkInvalid'));
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`/nif/public/${token}`);
        setValid(res.data.valid);
        setAlreadySubmitted(res.data.alreadySubmitted);
        setSiteName(res.data.siteName || '');
      } catch (err: unknown) {
        setValid(false);
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          t('nif.linkExpired');
        setErrorMsg(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, t]);

  const patch = useCallback((field: keyof NifFormData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const patchEndereco = useCallback((field: keyof NifFormData['endereco'], value: string) => {
    setFormData((prev) => ({
      ...prev,
      endereco: { ...prev.endereco, [field]: value }
    }));
  }, []);

  const buscarCEP = async (cep: string) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) return;

    setCepLoading(true);
    try {
      const response = await axios.get(`/nif/cep/${cleanCep}`);
      const data = response.data;
      setFormData((prev) => ({
        ...prev,
        endereco: {
          ...prev.endereco,
          cep: data.cep,
          logradouro: data.logradouro,
          bairro: data.bairro,
          cidade: data.cidade,
          estado: data.estado,
          uf: data.uf
        }
      }));
    } catch {
      setStepError(t('nif.errors.cepLookup'));
    } finally {
      setCepLoading(false);
    }
  };

  const validateFiles = (): string | null => {
    if (documentoPassaporte && documentoPassaporte.size > MAX_FILE_BYTES) {
      return t('nif.fileTooBig', { maxMb: MAX_FILE_MB });
    }
    if (documentoComprovanteResidencia && documentoComprovanteResidencia.size > MAX_FILE_BYTES) {
      return t('nif.fileTooBig', { maxMb: MAX_FILE_MB });
    }
    return null;
  };

  const goNext = () => {
    setStepError('');
    const fileErr = currentStep === 'documents' ? validateFiles() : null;
    if (fileErr) {
      setStepError(fileErr);
      return;
    }
    const err = validateNifStep(currentStep, formData, t);
    if (err) {
      setStepError(err);
      return;
    }
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };

  const goBack = () => {
    setStepError('');
    setStepIndex((i) => Math.max(0, i - 1));
  };

  const handleSubmit = async () => {
    setStepError('');
    const fileErr = validateFiles();
    if (fileErr) {
      setStepError(fileErr);
      return;
    }
    if (!formData.consentimentoLGPD) {
      setStepError(t('nif.errors.consent'));
      return;
    }

    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('nome', formData.nome);
      form.append('sobrenome', formData.sobrenome);
      form.append('dataNascimento', formData.dataNascimento);
      form.append('email', formData.email);
      form.append('passaporteNumero', formData.passaporteNumero);
      form.append('passaporteValidade', formData.passaporteValidade);
      form.append('endereco', JSON.stringify(formData.endereco));
      form.append('consentimentoLGPD', 'true');
      if (documentoPassaporte) form.append('documentoPassaporte', documentoPassaporte);
      if (documentoComprovanteResidencia) {
        form.append('documentoComprovanteResidencia', documentoComprovanteResidencia);
      }

      await axios.post(`/nif/public/${token}/submit`, form);
      setDone(true);
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t('nif.submitError');
      setStepError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const fileInputClass =
    'block w-full max-w-full min-w-0 text-base sm:text-sm text-gray-700 file:mr-3 file:rounded-lg file:border-0 file:bg-primary-50 file:px-4 file:py-3 file:text-base file:font-medium file:text-primary-700 hover:file:bg-primary-100';

  const renderStep = () => {
    switch (currentStep as NifWizardStepId) {
      case 'personal':
        return (
          <div className={stepFieldsClass}>
            <TextField
              label={t('nif.nome')}
              value={formData.nome}
              onChange={(v) => patch('nome', v)}
              required
              autoComplete="given-name"
            />
            <TextField
              label={t('nif.sobrenome')}
              value={formData.sobrenome}
              onChange={(v) => patch('sobrenome', v)}
              required
              autoComplete="family-name"
            />
            <TextField
              label={t('nif.dataNascimento')}
              type="date"
              value={formData.dataNascimento}
              onChange={(v) => patch('dataNascimento', v)}
              required
            />
            <TextField
              label={t('nif.email')}
              type="email"
              value={formData.email}
              onChange={(v) => patch('email', v)}
              required
              inputMode="email"
              autoComplete="email"
            />
          </div>
        );

      case 'passport':
        return (
          <div className={stepFieldsClass}>
            <TextField
              label={t('nif.passaporteNumero')}
              value={formData.passaporteNumero}
              onChange={(v) => patch('passaporteNumero', v)}
              required
            />
            <TextField
              label={t('nif.passaporteValidade')}
              type="date"
              value={formData.passaporteValidade}
              onChange={(v) => patch('passaporteValidade', v)}
              required
            />
          </div>
        );

      case 'documents':
        return (
          <div className={stepFieldsClass}>
            <p className="text-base sm:text-sm text-gray-600 leading-relaxed">
              {t('nif.docsHint', { maxMb: MAX_FILE_MB })}
            </p>
            <label className="visa-form-field block">
              <FieldLabel>{t('nif.passaporteCopy')}</FieldLabel>
              <input
                type="file"
                accept={ACCEPT_DOCS}
                onChange={(e) => setDocumentoPassaporte(e.target.files?.[0] ?? null)}
                className={fileInputClass}
              />
              {documentoPassaporte && (
                <p className="mt-2 text-sm text-gray-500 break-all">{documentoPassaporte.name}</p>
              )}
            </label>
            <label className="visa-form-field block">
              <FieldLabel>{t('nif.comprovante')}</FieldLabel>
              <input
                type="file"
                accept={ACCEPT_DOCS}
                onChange={(e) => setDocumentoComprovanteResidencia(e.target.files?.[0] ?? null)}
                className={fileInputClass}
              />
              {documentoComprovanteResidencia && (
                <p className="mt-2 text-sm text-gray-500 break-all">
                  {documentoComprovanteResidencia.name}
                </p>
              )}
            </label>
          </div>
        );

      case 'address':
        return (
          <div className={stepFieldsClass}>
            <label className="visa-form-field block">
              <FieldLabel required>{t('nif.cep')}</FieldLabel>
              <input
                type="text"
                value={formData.endereco.cep}
                onChange={(e) => {
                  const value = e.target.value;
                  patchEndereco('cep', value);
                  if (value.replace(/\D/g, '').length === 8) buscarCEP(value);
                }}
                placeholder="00000-000"
                maxLength={9}
                className="visa-form-input"
              />
              {cepLoading && (
                <p className="mt-1.5 text-sm text-gray-500">{t('nif.cepLoading')}</p>
              )}
            </label>
            <TextField
              label={t('nif.logradouro')}
              value={formData.endereco.logradouro}
              onChange={(v) => patchEndereco('logradouro', v)}
              required
            />
            <TextField
              label={t('nif.numero')}
              value={formData.endereco.numero}
              onChange={(v) => patchEndereco('numero', v)}
              required
            />
            <TextField
              label={t('nif.complemento')}
              value={formData.endereco.complemento}
              onChange={(v) => patchEndereco('complemento', v)}
            />
            <TextField
              label={t('nif.bairro')}
              value={formData.endereco.bairro}
              onChange={(v) => patchEndereco('bairro', v)}
              required
            />
            <TextField
              label={t('nif.cidade')}
              value={formData.endereco.cidade}
              onChange={(v) => patchEndereco('cidade', v)}
              required
            />
            <TextField
              label={t('nif.estado')}
              value={formData.endereco.estado}
              onChange={(v) => patchEndereco('estado', v)}
              required
            />
            <TextField
              label={t('nif.uf')}
              value={formData.endereco.uf}
              onChange={(v) => patchEndereco('uf', v.toUpperCase())}
              required
              maxLength={2}
            />
          </div>
        );

      case 'consent':
        return (
          <div className={stepFieldsClass}>
            <div className="rounded-xl border-2 border-gray-100 bg-gray-50 p-4 sm:p-5 space-y-4 text-base sm:text-sm text-gray-700 leading-relaxed">
              <p>
                <strong>{t('nif.lgpdIntro')}</strong>
              </p>
              <p>
                <strong>{t('nif.lgpdAuth')}</strong>
              </p>
              <ul className="space-y-2 list-disc pl-5">
                <li>{t('nif.lgpdB1')}</li>
                <li>{t('nif.lgpdB2')}</li>
                <li>{t('nif.lgpdB3')}</li>
              </ul>
              <p>
                <strong>{t('nif.lgpdConsent')}</strong>
              </p>
              <label className="flex items-start gap-3 cursor-pointer rounded-xl border-2 border-gray-200 bg-white px-4 py-3.5 min-h-[52px] has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50">
                <input
                  type="checkbox"
                  checked={formData.consentimentoLGPD}
                  onChange={(e) => patch('consentimentoLGPD', e.target.checked)}
                  className="check-input mt-1 shrink-0"
                />
                <span className="font-medium text-gray-900">{t('nif.lgpdAccept')}</span>
              </label>
            </div>
          </div>
        );

      case 'review':
        return (
          <div className={`${stepFieldsClass} text-gray-800`}>
            <p className="text-base sm:text-sm text-gray-600 leading-relaxed">{t('nif.reviewIntro')}</p>
            <div className="rounded-xl border-2 border-gray-100 bg-gray-50 p-4 sm:p-5 space-y-5 max-h-[min(50vh,480px)] sm:max-h-[28rem] overflow-y-auto overflow-x-hidden overscroll-contain">
              {reviewSections.map((section) => (
                <div key={section.title} className="min-w-0">
                  <h3 className="font-semibold text-gray-900 text-base mb-2.5">{section.title}</h3>
                  <dl className="space-y-2.5">
                    {section.lines.map((row) => (
                      <div
                        key={`${section.title}-${row.label}`}
                        className="min-w-0 border-b border-gray-200/80 pb-2.5 last:border-0 last:pb-0"
                      >
                        <dt className="text-sm sm:text-xs font-medium text-gray-500 mb-0.5">
                          {row.label}
                        </dt>
                        <dd className="text-base sm:text-sm text-gray-900 font-medium break-words">
                          {row.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" aria-label={t('common.loading')} />
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 p-4 sm:p-6">
        <div className="max-w-md text-center bg-white rounded-xl shadow-lg p-8">
          <Shield className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{t('nif.linkUnavailable')}</h1>
          <p className="text-gray-600">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (alreadySubmitted && !done) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 p-4 sm:p-6">
        <div className="max-w-md text-center bg-white rounded-xl shadow-lg p-8">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{t('nif.alreadySubmittedTitle')}</h1>
          <p className="text-gray-600">{t('nif.alreadySubmittedMsg', { siteName: displaySiteName })}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className={`flex items-center justify-center p-4 sm:p-6 ${pageShell}`}>
        <div className="max-w-md w-full text-center bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <CheckCircle className="w-14 h-14 text-primary-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('nif.successTitle')}</h1>
          <p className="text-gray-600">{t('nif.successMsg', { siteName: displaySiteName })}</p>
        </div>
      </div>
    );
  }

  const navButtons = (
    <>
      <Button variant="ghost" className="flex-1 sm:flex-none"
        type="button"
        onClick={goBack}
        disabled={stepIndex === 0}>
        <ChevronLeft className="w-4 h-4 shrink-0" /> {t('nif.prev')}
      </Button>
      {currentStep === 'review' ? (
        <Button className="flex-1 sm:flex-none"
          type="button"
          onClick={handleSubmit}
          disabled={submitting}>
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {t('nif.submit')}
        </Button>
      ) : (
        <Button className="flex-1 sm:flex-none"
          type="button"
          onClick={goNext}>
          {t('nif.next')} <ChevronRight className="w-4 h-4 shrink-0" />
        </Button>
      )}
    </>
  );

  return (
    <div
      className={`${pageShell} pb-[max(1rem,env(safe-area-inset-bottom))]`}
    >
      <div className="mx-auto w-full max-w-6xl px-4 py-5 sm:px-6 sm:py-8 lg:py-10">
        <header className="text-center text-gray-900 mb-5 sm:mb-8">
          <div className="flex justify-center mb-3 sm:mb-4">
            <SiteLogo
              size="lg"
              nameClassName="font-bold text-gray-900 text-xl sm:text-2xl"
              className="justify-center"
            />
          </div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold leading-tight px-1">
            {t('nif.title')}
          </h1>
          <p className="text-gray-600 text-sm sm:text-base mt-2 max-w-lg mx-auto px-2">
            {t('nif.subtitle')}
          </p>
          <div className="flex justify-center mt-4 lg:hidden">
            <LanguageSwitcher compact variant="floating" className="shadow-md" />
          </div>
        </header>

        <div className="flex flex-col items-stretch lg:flex-row lg:items-start lg:justify-center lg:gap-5 xl:gap-8">
          <div className="w-full max-w-2xl mx-auto lg:mx-0 min-w-0 flex-1">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-full">
              <div className="px-4 pt-4 sm:px-6 sm:pt-6">
                <div className="flex justify-between text-xs sm:text-sm text-gray-500 mb-2">
                  <span>{t('nif.stepOf', { current: stepIndex + 1, total: steps.length })}</span>
                  <span className="font-medium text-primary-600">{Math.round(progress)}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
                  <div
                    className="h-full bg-primary-600 transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>

                <div
                  className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 lg:hidden scrollbar-none snap-x snap-mandatory"
                  aria-hidden
                >
                  {steps.map((step, i) => {
                    const active = i === stepIndex;
                    const completed = i < stepIndex;
                    return (
                      <div
                        key={step}
                        className={`shrink-0 snap-center w-8 h-8 rounded-full text-xs font-semibold flex items-center justify-center transition-colors ${
                          active
                            ? 'bg-primary-600 text-white'
                            : completed
                              ? 'bg-primary-100 text-primary-700'
                              : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {i + 1}
                      </div>
                    );
                  })}
                </div>

                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mt-3 sm:mt-4 leading-snug">
                  {t(`nif.steps.${currentStep}`)}
                </h2>
              </div>

              <div className="px-4 py-5 sm:px-6 sm:py-6 overflow-x-hidden min-w-0">
                {renderStep()}
              </div>

              {stepError && (
                <p
                  className="px-4 sm:px-6 pb-2 text-base sm:text-sm font-medium text-red-600"
                  role="alert"
                >
                  {stepError}
                </p>
              )}

              <div className="hidden sm:flex px-6 pb-6 justify-between gap-3">{navButtons}</div>
            </div>

            <div
              className="fixed inset-x-0 bottom-0 z-30 sm:hidden border-t border-gray-200/90 bg-white/95 backdrop-blur-md px-4 pt-3 shadow-[0_-8px_30px_rgba(0,0,0,0.12)]"
              style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
            >
              <div className="flex gap-3 max-w-2xl mx-auto">{navButtons}</div>
            </div>
            <div className="h-[5.5rem] sm:hidden" aria-hidden />

            <p className="text-center text-gray-500 text-xs sm:text-sm mt-4 sm:mt-6 px-2 pb-24 sm:pb-0">
              {t('nif.privacy')}
            </p>
          </div>

          <aside
            className="hidden lg:flex shrink-0 sticky top-8 self-start flex-col items-center"
            aria-label={t('nif.languagePanel', { defaultValue: 'Idioma' })}
          >
            <LanguageSwitcher orientation="vertical" variant="floating" compact />
          </aside>
        </div>
      </div>
    </div>
  );
};

export default NIF;
